/**
 * Configuration for BuildingSwitcher component visibility
 * Controls which paths should show or hide the BuildingSwitcher
 */

export interface PathConfig {
  // Regex patterns for paths where BuildingSwitcher should be shown
  allowedPaths: RegExp[]
  // Regex patterns for paths where BuildingSwitcher should be hidden
  disallowedPaths: RegExp[]
}

/**
 * BuildingSwitcher Path Configuration
 *
 * - allowedPaths: Component will ONLY show on these paths
 * - disallowedPaths: Component will be hidden on these paths (takes precedence over allowedPaths)
 */
export const buildingSwitcherConfig: PathConfig = {
  // Paths where BuildingSwitcher SHOULD be shown
  allowedPaths: [
    /^\/app\/applicant/, // HR Applicant pages
    /^\/app\/schedule/, // Schedule management
    /^\/app\/payroll/, // Payroll management
    /^\/app\/patrol/, // Patrol management
    /^\/app\/request/, // Request management
    /^\/app\/user/, // User management
    /^\/app$/, // Dashboard root
  ],

  // Paths where BuildingSwitcher SHOULD be hidden
  disallowedPaths: [
    /^\/app\/role/, // Role management - tidak perlu building context
    /^\/app\/app-setting/, // App settings - global settings
    /^\/app\/bq-master-item/, // BQ Master Item - global configuration
    /^\/app\/payroll\/create-im/, // IM creation - tidak perlu building switcher
    /^\/app\/payroll\/detail-im/, // IM detail - tidak perlu building switcher
    /^\/app\/payroll\/edit-im/, // IM edit - tidak perlu building switcher

    // Patrol
    /^\/app\/patrol\/create-patrol/, // Patrol creation - tidak perlu building switcher
    /^\/app\/patrol\/edit-patrol/, // Patrol edit - tidak perlu building switcher
    /^\/app\/patrol\/detail-patrol/, // Patrol detail - tidak perlu building switcher
    /^\/app\/patrol\/area-and-assets\/edit-detail-asset/, // Patrol area and asset edit - tidak perlu building switcher
    /^\/app\/patrol\/area-and-assets\/detail-detail-asset/, // Patrol area and asset detail - tidak perlu building switcher

    // Request
    /^\/app\/request\/create-request/, // Request creation - tidak perlu building switcher
    /^\/app\/request\/edit-request/, // Request edit - tidak perlu building switcher
    /^\/app\/request\/detail-request/, // Request detail - tidak perlu building switcher
    /^\/app\/request\/edit-list-approval/, // Request list approval - tidak perlu building switcher

    // Panic Button
    /^\/app\/panic-button\/add/, // Panic button creation - tidak perlu building switcher
    /^\/app\/panic-button\/detail-panic-button/, // Panic button detail - tidak perlu building switcher

    // Building
    /^\/app\/building\/create-building/, // Building creation - tidak perlu building switcher
    /^\/app\/building\/edit-building/, // Building edit - tidak perlu building switcher
    // /^\/app\/building/, // Building detail - tidak perlu building switcher (Removed this line to allow detail-building)

    // BQ
    /^\/app\/building\/detail-building\/add-bq/, // BQ creation - tidak perlu building switcher
    /^\/app\/building\/detail-building\/add-building-position/, // Building position creation - tidak perlu building switcher
    /^\/app\/building\/detail-building\/update-building-position/, // Building position creation - tidak perlu building switcher
    /^\/app\/building\/detail-building\/detail-bq/, // BQ detail - tidak perlu building switcher

    // Applicant
    /^\/app\/applicant/,

    // Schedule
    /^\/app\/schedule\/add/, // Schedule creation - tidak perlu building switcher
    /^\/app\/schedule\/edit/, // Schedule edit - tidak perlu building switcher
    /^\/app\/schedule\/detail-attendance/, // Attendance detail - tidak perlu building switcher

    /^\/app\/schedule\/add-shift-schedule/, // Shift schedule creation - tidak perlu building switcher
    /^\/app\/schedule\/edit-shift-schedule/, // Shift schedule edit - tidak perlu building switcher
    /^\/app\/schedule\/detail-shift-schedule/, // Shift schedule detail - tidak perlu building switcher

    /^\/app\/schedule\/add-group-schedule/, // Group schedule creation - tidak perlu building switcher
    /^\/app\/schedule\/update-group-schedule/, // Group schedule edit - tidak perlu building switcher
    /^\/app\/schedule\/detail-group-schedule/, // Group schedule detail - tidak perlu building switcher

    // Payroll
    /^\/app\/payroll\/detail-payroll/, // Payroll detail - tidak perlu building switcher

    /^\/app\/payroll\/create-im/, // IM creation - tidak perlu building switcher
    /^\/app\/payroll\/edit-im/, // IM edit - tidak perlu building switcher
    /^\/app\/payroll\/detail-im/, // IM detail - tidak perlu building switcher

    /^\/app\/payroll\/create-deduction/, // Deduction creation - tidak perlu building switcher

    /^\/auth/, // Authentication pages
    /^\/login/, // Login page
    /^\/error/, // Error pages
  ],
}

/**
 * Check if BuildingSwitcher should be visible for the given path
 *
 * Logic:
 * 1. If path matches any disallowedPaths -> hide (false)
 * 2. If path matches any allowedPaths -> show (true)
 * 3. If no match in either -> hide by default (false)
 */
export const shouldShowBuildingSwitcher = (pathname: string): boolean => {
  const { allowedPaths, disallowedPaths } = buildingSwitcherConfig

  // First check disallowed paths (higher priority)
  const isDisallowed = disallowedPaths.some((pattern) => pattern.test(pathname))
  if (isDisallowed) {
    return false
  }

  // Then check allowed paths
  const isAllowed = allowedPaths.some((pattern) => pattern.test(pathname))
  return isAllowed
}

/**
 * Helper function to add new path patterns dynamically
 */
export const addAllowedPath = (pattern: RegExp): void => {
  buildingSwitcherConfig.allowedPaths.push(pattern)
}

export const addDisallowedPath = (pattern: RegExp): void => {
  buildingSwitcherConfig.disallowedPaths.push(pattern)
}

/**
 * Helper function to remove path patterns
 */
export const removeAllowedPath = (pattern: RegExp): void => {
  const index = buildingSwitcherConfig.allowedPaths.findIndex(
    (p) => p.source === pattern.source
  )
  if (index > -1) {
    buildingSwitcherConfig.allowedPaths.splice(index, 1)
  }
}

export const removeDisallowedPath = (pattern: RegExp): void => {
  const index = buildingSwitcherConfig.disallowedPaths.findIndex(
    (p) => p.source === pattern.source
  )
  if (index > -1) {
    buildingSwitcherConfig.disallowedPaths.splice(index, 1)
  }
}
